import { useMemo } from "react";
import { Tree } from "antd";
import type { DataNode } from "antd/es/tree";
import { useNavigate } from "react-router-dom";
import { categoryEntries, totalCount, type CategoryNode } from "@/data/category-tree.js";
import { CDN_URL } from "@/theme.js";

function toTreeData(entries: [number, CategoryNode][]): DataNode[] {
  function build(node: CategoryNode): DataNode {
    const children = node.children?.map(([, child]) => build(child)) ?? undefined;
    return {
      key: String(node.id),
      title: (
        <span className="tn-sidebar__row">
          <span className="tn-sidebar__label">
            <img src={CDN_URL + node.thumb} alt="" loading="lazy" className="tn-sidebar__thumb" />
            <span className="tn-sidebar__name">{node.category}</span>
          </span>
          <span className="tn-sidebar__count">{Number(node.count).toLocaleString()}</span>
        </span>
      ),
      children,
    };
  }
  return entries.filter(([, node]) => !node.child).map(([, node]) => build(node));
}

function findCategoryNode(entries: [number, CategoryNode][], id: number): CategoryNode | null {
  for (const [, node] of entries) {
    if (node.id === id) return node;
    if (node.children) {
      const child = findCategoryNode(node.children, id);
      if (child) return child;
    }
  }
  return null;
}

export function Sidebar() {
  const navigate = useNavigate();
  const treeData = useMemo(() => toTreeData(categoryEntries), []);

  function handleSelect(keys: React.Key[]) {
    const key = keys[0];
    if (!key) return;
    const id = Number(key);
    const node = findCategoryNode(categoryEntries, id);
    if (!node) return;
    const path = node.children
      ? `/category/${node.category}/${node.id}`
      : `/textures/${node.category}/${node.id}`;
    navigate(path);
  }

  return (
    <nav
      aria-label="Categories"
      className="tn-sidebar"
      style={{
        padding: "20px 12px 28px",
        height: "100%",
        overflow: "auto",
        background: "transparent",
      }}
    >
      <div
        style={{
          padding: "0 8px 12px",
          marginBottom: 8,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--tn-border-faint)",
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255, 255, 255, 0.5)",
          }}
        >
          Browse
        </span>
        <span
          style={{
            fontSize: 11,
            color: "rgba(255, 255, 255, 0.4)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {totalCount.toLocaleString()} files
        </span>
      </div>
      <Tree treeData={treeData} onSelect={handleSelect} blockNode defaultExpandAll={false} />
    </nav>
  );
}
